import React, { type FunctionComponent, useEffect } from 'react';

import { FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { TEXT_VARIANTS } from '../../../themes/MaterialTheme';
import {
    type AssistantType,
    type DocumentType,
    type GradebookType,
    type HostType,
    type ProctoringType,
    type RosterType,
    type StudentType
} from '../../../types/adminConfig';
import AdminAuthContainer from '../AdminAuthContainer';
import AdminSettingsContainer from '../AdminSettingsContainer';
import SaveButton from '../components/SaveButton';
import {
    AssistantsTitle,
    HostTitle,
    StudentsTitle,
    RosterTitle,
    GradebookTitle,
    DocumentTitle,
    ProctoringTitle
} from '../config/adminSettingsConfig';
import StyledFormTitle from '../styles/form-title';
import validateTerminologySettings, {
    validateAssistantsTitle,
    validateDocumentTitle,
    validateGradebookTitle,
    validateHostTitle,
    validateProctoringTitle,
    validateRosterTitle,
    validateStudentTitle
} from '../utils/validateTerminologySettings';

const StyledFormGroupContainer = styled.div`
    width: 316px;
    margin-bottom: 20px;
`;

const StyledFormControl = withStyles(() => ({
    root: {
        marginTop: '13px',
        width: '100%'
    }
}))(FormControl);

const StyledFormLabel = withStyles(() => ({
    root: {
        fontWeight: 700
    }
}))(Typography);

const StyledInputLabel = withStyles(() => ({
    root: {
        marginTop: '33px',
        color: TEXT_VARIANTS.primary
    }
}))(InputLabel);

const TerminologySettings: FunctionComponent = () => {
    const { adminSettings, school, setAdminSettings, isSavingSettingsData, handleSaveSettings } =
        useContainer(AdminSettingsContainer);
    const { isAdmin } = useContainer(AdminAuthContainer);
    const { terminologySettings, organizationType } = adminSettings;
    const { t } = useTranslation('admin_settings');

    useEffect(() => {
        const validatedTerminology = validateTerminologySettings(terminologySettings, organizationType);
        setAdminSettings({ ...adminSettings, terminologySettings: validatedTerminology });
        // eslint-disable-next-line
    }, []);

    const renderMenuOptions = (titles: Record<string, string>): JSX.Element[] => {
        const composedMenuItems = Object.values(titles).map((title) => (
            <MenuItem key={title} value={title}>
                {t(`menuOptions.${title}`)}
            </MenuItem>
        ));
        return composedMenuItems;
    };

    return (
        <>
            <StyledFormTitle>{t('terminology.title')}</StyledFormTitle>
            <StyledFormLabel variant="body1">{t('terminology.formLabel')}</StyledFormLabel>
            <StyledFormGroupContainer>
                <StyledInputLabel id="teacher-title-label">{t('terminology.hostLabel')}</StyledInputLabel>
                <StyledFormControl variant="outlined">
                    <Select
                        labelId="teacher-title-label"
                        id="teacher-title"
                        value={validateHostTitle(terminologySettings.hostTitle, organizationType)}
                        onChange={(event) => {
                            terminologySettings.hostTitle = event.target.value as HostType;
                            setAdminSettings({ ...adminSettings });
                        }}
                        disabled={!isAdmin}
                    >
                        {renderMenuOptions(HostTitle)}
                    </Select>
                </StyledFormControl>
                <StyledInputLabel id="assistants-title-label">{t('terminology.assistantLabel')}</StyledInputLabel>
                <StyledFormControl variant="outlined">
                    <Select
                        labelId="assistants-title-label"
                        id="assistants-title"
                        value={validateAssistantsTitle(terminologySettings.assistantsTitle, organizationType)}
                        onChange={(event) => {
                            terminologySettings.assistantsTitle = event.target.value as AssistantType;
                            setAdminSettings({ ...adminSettings });
                        }}
                        disabled={!isAdmin}
                    >
                        {renderMenuOptions(AssistantsTitle)}
                    </Select>
                </StyledFormControl>
                <StyledInputLabel id="students-title-label">{t('terminology.studentsLabel')}</StyledInputLabel>
                <StyledFormControl variant="outlined">
                    <Select
                        labelId="students-title-label"
                        id="students-title"
                        value={validateStudentTitle(terminologySettings.studentsTitle, organizationType)}
                        onChange={(event) => {
                            terminologySettings.studentsTitle = event.target.value as StudentType;
                            setAdminSettings({ ...adminSettings });
                        }}
                        disabled={!isAdmin}
                    >
                        {renderMenuOptions(StudentsTitle)}
                    </Select>
                </StyledFormControl>
                <StyledInputLabel id="roster-title-label">{t('terminology.rosterLabel')}</StyledInputLabel>
                <StyledFormControl variant="outlined">
                    <Select
                        labelId="roster-title-label"
                        id="roster-title"
                        value={validateRosterTitle(terminologySettings.rosterTitle, organizationType)}
                        onChange={(event) => {
                            terminologySettings.rosterTitle = event.target.value as RosterType;
                            setAdminSettings({ ...adminSettings });
                        }}
                        disabled={!isAdmin}
                    >
                        {renderMenuOptions(RosterTitle)}
                    </Select>
                </StyledFormControl>
                <StyledInputLabel id="gradebook-title-label">{t('terminology.gradebookLabel')}</StyledInputLabel>
                <StyledFormControl variant="outlined">
                    <Select
                        labelId="gradebook-title-label"
                        id="gradebook-title"
                        value={validateGradebookTitle(terminologySettings.gradebookTitle, organizationType)}
                        onChange={(event) => {
                            terminologySettings.gradebookTitle = event.target.value as GradebookType;
                            setAdminSettings({ ...adminSettings });
                        }}
                        disabled={!isAdmin}
                    >
                        {renderMenuOptions(GradebookTitle)}
                    </Select>
                </StyledFormControl>
                <StyledInputLabel id="document-title-label">{t('terminology.documentLabel')}</StyledInputLabel>
                <StyledFormControl variant="outlined">
                    <Select
                        labelId="document-title-label"
                        id="document-title"
                        value={validateDocumentTitle(terminologySettings.documentTitle, organizationType)}
                        onChange={(event) => {
                            terminologySettings.documentTitle = event.target.value as DocumentType;
                            setAdminSettings({ ...adminSettings });
                        }}
                        disabled={!isAdmin}
                    >
                        {renderMenuOptions(DocumentTitle)}
                    </Select>
                </StyledFormControl>
                {!school?.isTeams && (
                    <>
                        <StyledInputLabel id="proctoring-title-label">
                            {t('terminology.proctoringLabel')}
                        </StyledInputLabel>
                        <StyledFormControl variant="outlined">
                            <Select
                                labelId="proctoring-title-label"
                                id="proctoring-title"
                                value={validateProctoringTitle(terminologySettings.proctoringTitle, organizationType)}
                                onChange={(event) => {
                                    terminologySettings.proctoringTitle = event.target.value as ProctoringType;
                                    setAdminSettings({ ...adminSettings });
                                }}
                                disabled={!isAdmin}
                            >
                                {renderMenuOptions(ProctoringTitle)}
                            </Select>
                        </StyledFormControl>
                    </>
                )}
            </StyledFormGroupContainer>
            <SaveButton isSaving={isSavingSettingsData} onClick={handleSaveSettings} />
        </>
    );
};

export default TerminologySettings;
